const CHECK_INTERVAL = 100;
const RE_WHITESPACE = /[\t\n\r ]/;

/**
 * WeakMap preferred, but all items cleared on document ready regardless
 * @type { Map<HTMLElement, () => void> }
 */
const pendingParsedMap = new Map();
/** @type { number | undefined } */
let checkIntervalId;

/**
 * Notify "element" when its attributes and children are fully parsed.
 * During page load, HTML elements are incrementally parsed,
 * and there is no DOM API for determining whether an element has been parsed until the full document is ready.
 * Instead, we check if an element or one of its parents has a sibling after as a signal for fully parsed.
 *
 * @param { HTMLElement } element
 * @returns { Promise<void> }
 */
export function whenElementParsed(element) {
  return new Promise((resolve) => {
    if (document.readyState !== 'loading' || isElementParsed(element)) {
      return resolve();
    }
    if (checkIntervalId === undefined) {
      checkIntervalId = window.setInterval(check, CHECK_INTERVAL);
      window.addEventListener('DOMContentLoaded', documentReady);
    }
    if (!pendingParsedMap.has(element)) {
      pendingParsedMap.set(element, resolve);
    } else {
      // Wrap existing resolve
      const pendingResolve = pendingParsedMap.get(element);

      pendingParsedMap.set(element, () => {
        // @ts-ignore
        pendingResolve();
        resolve();
      });
    }
  });
}

/**
 * Check all elements waiting for parse resolution
 */
function check() {
  for (const [element, resolve] of pendingParsedMap) {
    if (isElementParsed(element)) {
      pendingParsedMap.delete(element);
      resolve();
    }
  }
}

/**
 * "DOMContentLoaded" handler.
 * Disable checking and resolve all pending Promises.
 */
function documentReady() {
  window.removeEventListener('DOMContentLoaded', documentReady);
  clearInterval(checkIntervalId);
  checkIntervalId = undefined;

  for (const resolve of pendingParsedMap.values()) {
    resolve();
  }

  pendingParsedMap.clear();
}

/**
 * Check if "element" has been parsed.
 * Checks if an element, or one of it's ancestors, has trailing whitespace,
 * which is identified by a text node "nextSibling".
 * Elements that have been dynamically inserted during loading will not have trailing whitespace,
 * and will therefore not trigger a false positive here.
 *
 * @param { HTMLElement } element
 * @returns { boolean }
 */
function isElementParsed(element) {
  /** @type {HTMLElement | null} */
  let target = element;

  while (target && target !== document.body) {
    const nextSibling = target.nextSibling;

    if (
      nextSibling !== null &&
      nextSibling.nodeType === 3 &&
      (nextSibling.textContent === null || RE_WHITESPACE.test(nextSibling.textContent))
    ) {
      return true;
    }

    target = target.parentElement;
  }

  return false;
}
